import { Moment } from 'moment';

import { DeliveryPoint } from '../account/delivery-point.model';
import { HtmlTooltipData } from '../html-tooltip';
import { Unit } from '../types/all';
import { InvoiceState } from '../types/invoice-state';
import { InvoiceActionLog } from '../types/invoice.interface';
import { User } from '../user.model';

import { InvoiceItem } from './invoice-item.model';

export interface InvoiceVolumeQuality {
  realizedValidated: number;
  realized: number;
  declared: number;
  zero: number;
}

export interface SAP {
  invoiceNumber: string;
  editionDate: Moment;
  isPublishToSap: boolean;
  creditNumber?: string;
}

export interface Vat {
  amount: number;
  applicableOnAmount: number;
  exoneratedOnAmount: number;
  rate: number;
}

export interface VatInfo {
  full: Vat;
  reduced: Vat;
  none: Partial<Vat>;
}

export enum InvoiceTypology {
  all = 'all',
  standard = 'standard',
  final = 'final',
  deposit = 'deposit',
}

export enum InvoicePaymentStatus {
  paid = 'paid',
  unPaid = 'unPaid',
  overdue = 'overdue',
}

/**
 * Model for API InvoiceReplyModel Model
 */
export class Invoice {
  /**
   * Id of Invoice
   */
  id: number;

  /**
   * Month of the invoice
   */
  month: Moment;

  /**
   * Contract ID
   */
  contractId: number;

  /**
   *  Delivery point on which the invoice is applying
   */
  deliveryPoint: DeliveryPoint;

  /**
   * currency for amounts
   */
  currency = 'EUR';

  /**
   * Amount of the invoice (actually computed from items)
   */
  total: number;

  /**
   * Amount of the nvoice (vat excluded)
   */
  totalVatExcluded: number;

  /**
   * Volume
   */
  volume: number;

  /**
   * Volume unit, by default MWh
   */
  volumeUnit = Unit.MWh;

  /**
   * Quality of volume used to compute invoice
   */
  volumeQuality: InvoiceVolumeQuality;

  /**
   * Version date of the invoice
   */
  versionDate: Moment;

  /**
   * All items of the invoice.
   */
  items: InvoiceItem[] = [];

  /**
   * State of the invoice
   */
  state: InvoiceState;

  /**
   * Validator of the invoice
   */
  generatedBy: User;

  /**
   * Input information, must be re-send as is
   */
  inputs: [any];

  /**
   * SAP information as invoice number, invoice emission date...
   */
  sap: SAP;

  /**
   * The waaf status indicates whether an invoice is imported in wattson.
   */
  wattsonAsAFront: boolean;

  vatInfo: VatInfo;

  /**
   * The deadline of invoice payment.
   */
  paymentDeadline: Moment;

  /**
   * modification/status history
   */
  history: Invoice[];

  /** The typology of an invoice */
  typology: InvoiceTypology;

  /** The state of invoice if current or not  */
  isCurrent: boolean;
  showFinalButton = false;

  settings = {
    showVolume: true,
  };

  /** Invoice payment status */
  paymentStatus: InvoicePaymentStatus;

  selected: boolean;

  constructor(attributes: Partial<Invoice> = {}) {
    Object.assign(this, attributes);
  }

  get formatedMonth(): string {
    return this.month.format('YYYY-MM');
  }

  get sapNumber() {
    if (this.state === 'credit') return this.sap.creditNumber;
    return this.sap.invoiceNumber;
  }

  get iconString(): string {
    switch (this.state) {
      case InvoiceState.preliminary:
        return 'invoice-preliminary';
      case InvoiceState.credit:
        return 'invoice-credit';
      case InvoiceState.draft:
        return 'invoice-draft';
      case InvoiceState.validated:
        return 'invoice-validated';
      case InvoiceState.published:
        return 'invoice-published';
      case InvoiceState.unpublished:
        return 'invoice-unpublished';
      case InvoiceState['multiple-contracts']:
      case InvoiceState['outside-contracts']:
        return 'invoice-warning_triangle';
      case InvoiceState['estimation-report']:
      default:
        return 'invoice-warning_round';
    }
  }

  get stateLabel(): string {
    switch (this.state) {
      case InvoiceState.preliminary:
        return 'billing.history.preliminary';
      case InvoiceState.credit:
        return 'billing.history.credit';
      case InvoiceState.draft:
        return 'billing.history.draft';
      case InvoiceState.validated:
        return 'billing.history.validated';
      case InvoiceState.published:
        return 'billing.history.published';
      case InvoiceState.unpublished:
        return 'billing.history.unpublished';
      default:
        return '';
    }
  }

  get nm3Currency(): string {
    return this.volumeUnit === 'Nm3' ? 'c€' : '€';
  }

  canValidateCommoPrice(countries: string[], accountId: number): boolean {
    if (countries.includes(this.deliveryPoint?.site?.country) && accountId !== 944)
      return ![InvoiceState.preliminary, InvoiceState.validated].includes(this.state);
    return true;
  }

  canDownloadPdf(countries: string[], accountId: number): boolean {
    return !(countries.includes(this.deliveryPoint?.site?.country) && accountId !== 944 && this.state !== InvoiceState.published);
  }

  get tooltip() {
    const generatedBy = this.history ? this.history[this.history.length - 1]?.generatedBy : this.generatedBy;
    if (generatedBy) {
      return `- Last Modified By: ${generatedBy.email}`;
    }
    return null;
  }

  getActionStatus(logs: InvoiceActionLog) {
    if (logs[this.deliveryPoint.meteringPoint]) return logs[this.deliveryPoint.meteringPoint];
    return { status: null, message: null, details: null };
  }

  getToolTipData(logs: InvoiceActionLog): HtmlTooltipData {
    const data = this.getActionStatus(logs);
    const headerMap = new Map<string, string>();
    headerMap.set('message', data.message);
    headerMap.set('details', data.details);
    return new HtmlTooltipData({ headerMap });
  }

  get miscItemsHasError(): boolean {
    return this.items.some((item) => item.category === 'misc' && item.internalCode === 'ERROR');
  }

  hasPdf(): boolean {
    return (
      this.isInvoiceStateValid() || [InvoiceState.validated, InvoiceState.published].some((invoiceState) => this.state === invoiceState)
    );
  }

  private isInvoiceStateValid(): boolean {
    return ['notfound', 'multiple-contracts', 'outside-contract'].indexOf(this.state) === -1;
  }
}
